export type ReadingChallengeDayLog = {
  versesRead: number
  timeSpentSeconds: number
  goalCompleted: boolean
}

export type ReadingChallengeType = {
  dailyGoal: number
  currentVerse: number
  startingSurah: number
  startingVerse: number
  dailyLog: Record<string, ReadingChallengeDayLog>
  totalVersesRead: number
  totalTimeSeconds: number
}

export type DayStatus = 'completed' | 'partial' | 'missed' | 'today' | 'future'

const DAILY_LOG_MAX_DAYS = 7
const dayLabels = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']
const surahVerseCounts = [
  7, 286, 200, 176, 120, 165, 206, 75, 129, 109, 123, 111, 43, 52, 99, 128, 111, 110, 98,
  135, 112, 78, 118, 64, 77, 227, 93, 88, 69, 60, 34, 30, 73, 54, 45, 83, 182, 88, 75, 85,
  54, 53, 89, 59, 37, 35, 38, 29, 18, 45, 60, 49, 62, 55, 78, 96, 29, 22, 24, 13, 14, 11,
  11, 18, 12, 12, 30, 52, 52, 44, 28, 28, 20, 56, 40, 31, 50, 40, 46, 42, 29, 19, 36, 25,
  22, 17, 19, 26, 30, 20, 15, 21, 11, 8, 8, 19, 5, 8, 8, 11, 11, 8, 3, 9, 5, 4, 7, 3, 6,
  3, 5, 4, 5, 6,
]

/** Returns the web-compatible local date key used by reading challenge data. */
export function getTodayKey(date = new Date()): string {
  return formatDateKey(date)
}

/** Converts a surah and verse pair into the canonical 1–6236 verse number. */
export function surahVerseToGlobalVerse(surahId: number, verseNumber: number): number {
  return (
    surahVerseCounts.slice(0, surahId - 1).reduce((sum, count) => sum + count, 0) +
    verseNumber
  )
}

/** Returns the canonical verse count for a surah or zero for an invalid id. */
export function getSurahVerseCount(surahId: number): number {
  return surahVerseCounts[surahId - 1] || 0
}

/** Keeps only the seven most recent daily challenge entries. */
export function pruneDailyLog(
  dailyLog: Record<string, ReadingChallengeDayLog>,
  maxDays = DAILY_LOG_MAX_DAYS,
): Record<string, ReadingChallengeDayLog> {
  if (Object.keys(dailyLog).length <= maxDays) return dailyLog
  return Object.fromEntries(
    Object.entries(dailyLog)
      .sort(
        ([first], [second]) =>
          parseDateKey(second).getTime() - parseDateKey(first).getTime(),
      )
      .slice(0, maxDays),
  )
}

/** Records one verse while preserving the existing web challenge semantics. */
export function logVerseRead(
  currentData: ReadingChallengeType,
  date = getTodayKey(),
): ReadingChallengeType {
  const existingDay = currentData.dailyLog[date] ?? emptyDayLog()
  const versesRead = existingDay.versesRead + 1
  const dailyLog = pruneDailyLog({
    ...currentData.dailyLog,
    [date]: {
      ...existingDay,
      versesRead,
      goalCompleted: versesRead >= currentData.dailyGoal,
    },
  })

  return {
    ...currentData,
    currentVerse: Math.min(currentData.currentVerse + 1, 6236),
    dailyLog,
    totalVersesRead: currentData.totalVersesRead + 1,
  }
}

/** Adds reading time to the selected challenge day. */
export function logTimeSpent(
  currentData: ReadingChallengeType,
  seconds: number,
  date = getTodayKey(),
): ReadingChallengeType {
  const existingDay = currentData.dailyLog[date] ?? emptyDayLog()
  return {
    ...currentData,
    dailyLog: pruneDailyLog({
      ...currentData.dailyLog,
      [date]: {
        ...existingDay,
        timeSpentSeconds: existingDay.timeSpentSeconds + seconds,
      },
    }),
    totalTimeSeconds: currentData.totalTimeSeconds + seconds,
  }
}

/** Reports whether a challenge day has reached its configured goal. */
export function isGoalCompletedToday(
  dailyLog: Record<string, ReadingChallengeDayLog>,
  dailyGoal: number,
  date = getTodayKey(),
): boolean {
  return (dailyLog[date]?.versesRead ?? 0) >= dailyGoal
}

/** Returns the Monday-to-Sunday status used by the web challenge tracker. */
export function getWeeklyStatus(
  dailyLog: Record<string, ReadingChallengeDayLog>,
  dailyGoal: number,
  today = new Date(),
): { day: string; status: DayStatus; versesRead: number }[] {
  const currentDay = startOfDay(today)
  const monday = addDays(
    currentDay,
    currentDay.getDay() === 0 ? -6 : 1 - currentDay.getDay(),
  )

  return dayLabels.map((day, index) => {
    const date = addDays(monday, index)
    const dateKey = formatDateKey(date)
    const log = dailyLog[dateKey]
    const status = getDayStatus(log, date, currentDay, dailyGoal)
    return { day, status, versesRead: log?.versesRead ?? 0 }
  })
}

/** Counts consecutive goal-completion days ending today or yesterday. */
export function getCurrentStreak(
  dailyLog: Record<string, ReadingChallengeDayLog>,
  dailyGoal: number,
  today = new Date(),
): number {
  let currentDay = startOfDay(today)
  const todayLog = dailyLog[formatDateKey(currentDay)]
  if (!todayLog || todayLog.versesRead === 0) currentDay = addDays(currentDay, -1)

  let streak = 0
  for (let index = 0; index < 365; index += 1) {
    const log = dailyLog[formatDateKey(currentDay)]
    if (!log || log.versesRead < dailyGoal) break
    streak += 1
    currentDay = addDays(currentDay, -1)
  }
  return streak
}

/** Returns reading totals for the web-compatible today, week, or all period. */
export function getStatsForPeriod(
  data: ReadingChallengeType,
  period: 'today' | 'week' | 'all',
  today = new Date(),
): { versesRead: number; timeSeconds: number } {
  if (period === 'all') {
    return { versesRead: data.totalVersesRead, timeSeconds: data.totalTimeSeconds }
  }

  const currentDay = startOfDay(today)
  const firstDay = addDays(
    currentDay,
    currentDay.getDay() === 0 ? -6 : 1 - currentDay.getDay(),
  )
  const days =
    period === 'today'
      ? [currentDay]
      : Array.from({ length: 7 }, (_, index) => addDays(firstDay, index))
  return days.reduce(
    (summary, date) => {
      const log = data.dailyLog[formatDateKey(date)]
      return {
        versesRead: summary.versesRead + (log?.versesRead ?? 0),
        timeSeconds: summary.timeSeconds + (log?.timeSpentSeconds ?? 0),
      }
    },
    { versesRead: 0, timeSeconds: 0 },
  )
}

/** Creates the same empty challenge shape used by the web goal setup flow. */
export function createInitialChallenge(
  dailyGoal: number,
  startingSurah: number,
  startingVerse: number,
): ReadingChallengeType {
  return {
    dailyGoal,
    currentVerse: surahVerseToGlobalVerse(startingSurah, startingVerse),
    startingSurah,
    startingVerse,
    dailyLog: {},
    totalVersesRead: 0,
    totalTimeSeconds: 0,
  }
}

function emptyDayLog(): ReadingChallengeDayLog {
  return { versesRead: 0, timeSpentSeconds: 0, goalCompleted: false }
}

function getDayStatus(
  log: ReadingChallengeDayLog | undefined,
  date: Date,
  today: Date,
  dailyGoal: number,
): DayStatus {
  if (date > today) return 'future'
  if (date.getTime() === today.getTime())
    return log?.goalCompleted ? 'completed' : 'today'
  if (log?.goalCompleted || (log?.versesRead ?? 0) >= dailyGoal) return 'completed'
  return log && log.versesRead > 0 ? 'partial' : 'missed'
}

function formatDateKey(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}-${month}-${date.getFullYear()}`
}

function parseDateKey(value: string): Date {
  const [day, month, year] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addDays(date: Date, amount: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + amount)
  return startOfDay(next)
}
